import '../common/prototypes/array.prototypes';
import React, { FC } from 'react';
import { Route, BrowserRouter, Routes } from 'react-router-dom';
import Dashboard from 'pages/dashboard';
import SidebarLegacy from 'features/sidebar/sidebarlegacy';
import NotFound from 'pages/notFound';
import { Reports } from 'pages/reports';
import './app.css';
import Login from 'pages/login';
import { PrivateOutlet } from 'pages/privateOutlet';
import TopBar from 'features/topbar/topBar';
import Revenue from 'pages/revenue';
import { registerChartJs } from 'features/charts/chartjs';
import MmmModelledRoiPage from 'features/marketing-mix-modelling/modelled-roi/mmm-modelled-roi-page';
import { Modal } from 'features/modal/modal';
import Visitor from 'pages/visitor';
import Opportunities from 'pages/opportunities';
import Toast from 'features/toast/toast';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import PageContent from './page-content';
import MmmBudgetOptimiserPage from 'features/marketing-mix-modelling/budget-optimiser/mmm-budget-optimiser-page';
import { GeneralSettings } from 'pages/settings-general';
import { SettingsUploadPage } from 'pages/settings-upload';
import { SettingsWebhooksPage } from 'pages/settings-webhooks';
import { SettingsWebhooksCreatePage } from 'pages/settings-webhooks-create';
import { SettingsDataScienceTasksPage } from 'pages/settings-data-science-tasks';
import ConversionsForm from 'pages/conversions-form';
import ConversionsCall from 'pages/conversions-call';
import { SiteSetup } from 'pages/site-setup';
import { SetupCallTrackingSettings } from 'pages/setup-call-tracking';
import { HelpPage } from 'pages/help';
import { SettingsExclusionsPage } from 'pages/settings-exclusions';
import { SettingsExclusionsCreatePage } from 'pages/settings-exclusions-create';
import SettingsWebhookHistoryPage from 'pages/settings-webhooks-history';
import { Paths } from 'pages/paths';
import { PocOutlet } from 'pages/poc-outlet';
import { InternalPage } from 'pages/internal';
import AdvancedReportsPage from 'pages/advanced-reports-page';
import { CallRecordingPage } from 'pages/call-recording';
import { ToggleType } from 'features/internal/internal.models';
import { debugToggleByType } from 'features/internal/internal-settings.slice';
import SideBar from 'features/sidebar/sidebar';
import ConversionInfluenceInsightPage from 'pages/insights-conversion-influence';
import SuperUserOutlet from 'pages/super-user-outlet';
import SettingsWebPageViewSegmentationConfigPage from 'pages/settings-segmentation-config';
import { SettingsSegmentationConfigCreatePage } from 'pages/settings-segmentation-config-create';
import { SettingsSegmentationConfigEditPage } from 'pages/settings-segmentation-config-edit';
import { IntegrationsPage } from 'pages/integrations';
import { AddIntegrationsPage } from 'pages/integrations-add';
import { GoogleAdsSetupPage } from 'pages/integrations-google-ads-setup';
import SettingsCreateDataScienceTaskPage from 'pages/settings-data-science-tasks-create';
import SettingsEditDataScienceTaskPage from 'pages/settings-data-science-tasks-edit';
import { PublicOutlet } from 'pages/publicOutlet'; // Add the PublicOutlet import
import { PrivacyPolicyPage } from 'pages/privacy-policy';
import { RunIntegrationPage } from 'pages/integration-run';
import SettingsDataScienceTasksReviewPage from 'pages/settings-data-science-tasks-review';
import { FacebookAdsSetupPage } from 'pages/integrations-facebook-ads-setup';
import BudgetScenarioPlanner from 'pages/budget-scenario-planner';
import CampaignBudgetScenarioPlanner from 'pages/campaign-budget-scenario-planner';

const App: React.FC = () => {
  const { siteLabel } = useSelector((state: RootState) => state.selectedSite);
  const isNewSideBarEnabledDebugToggle = useSelector((state: RootState) =>
    debugToggleByType(state, ToggleType.DisplayNewSideBar)
  );

  const MainAppContainer: FC<Record<string, never>> = () => {
    return (
      <div className="bg-neutral-100 font-sans min-h-screen">
        {isNewSideBarEnabledDebugToggle?.enabled ? (
          <SideBar />
        ) : (
          <SidebarLegacy />
        )}
        <TopBar />

        {siteLabel !== '' ? (
          <>
            <Modal />
            <PageContent />
            <div className="clear:both"></div>
          </>
        ) : (
          <div></div>
        )}
        <div className="fixed bottom-5 right-5 z-20">
          <Toast />
        </div>
      </div>
    );
  };

  registerChartJs();

  return (
    <BrowserRouter>
      <Routes>
        {/* ✅ Public Routes */}
        <Route element={<PublicOutlet />}>
          <Route path="/login" element={<Login />} />
          <Route
            path="/:siteId/call-recording/:hash"
            element={<CallRecordingPage />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          {/* Add any additional public routes here */}
        </Route>

        {/* 🔒 Private Routes */}
        <Route path="/" element={<PrivateOutlet />}>
          <Route path="/" element={<MainAppContainer />}>
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="advanced-reports" element={<AdvancedReportsPage />} />
            <Route path="reports/:reportType" element={<Reports />} />

            <Route path="conversions">
              <Route path="form" element={<ConversionsForm />} />
              <Route path="forms" element={<ConversionsForm />} />
              <Route path="phone-call" element={<ConversionsCall />} />
              <Route path="phone-calls" element={<ConversionsCall />} />
            </Route>

            <Route path="visitors/:visitorId" element={<Visitor />} />
            <Route path="revenue" element={<Revenue />} />
            <Route path="opportunities" element={<Opportunities />} />
            <Route path="paths" element={<Paths />} />
            <Route path="insights">
              <Route
                path="conversion-influence"
                element={<ConversionInfluenceInsightPage />}
              />
            </Route>
            <Route path="site-setup" element={<SiteSetup />} />
            <Route
              path="setup-call-tracking"
              element={<SetupCallTrackingSettings />}
            />
            <Route path="help" element={<HelpPage />} />
            <Route path="settings">
              <Route path="general" element={<GeneralSettings />} />
              <Route path="imports" element={<SettingsUploadPage />} />
              <Route path="webhooks" element={<SettingsWebhooksPage />} />
              <Route
                path="webhooks/create"
                element={<SettingsWebhooksCreatePage />}
              />
              <Route
                path="webhooks/:webhookId/history"
                element={<SettingsWebhookHistoryPage />}
              />
              <Route path="exclusions" element={<SettingsExclusionsPage />} />
              <Route
                path="exclusions/create"
                element={<SettingsExclusionsCreatePage />}
              />
              <Route
                path="data-science/tasks"
                element={<SettingsDataScienceTasksPage />}
              />
              <Route
                path="data-science/tasks/create"
                element={<SettingsCreateDataScienceTaskPage />}
              />
              <Route
                path="data-science/tasks/:targetDataScienceFeature/edit"
                element={<SettingsEditDataScienceTaskPage />}
              />
              <Route
                path="data-science/tasks/:targetDataScienceFeature/review"
                element={<SettingsDataScienceTasksReviewPage />}
              />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route
                path="integrations/add"
                element={<AddIntegrationsPage />}
              />
              <Route
                path="integrations/setup/google-ads"
                element={<GoogleAdsSetupPage />}
              />
              <Route
                path="integrations/setup/facebook-ads"
                element={<FacebookAdsSetupPage />}
              />
              <Route element={<PocOutlet />}>
                <Route
                  path="segmentation-config"
                  element={<SettingsWebPageViewSegmentationConfigPage />}
                />
                <Route
                  path="segmentation-config/create"
                  element={<SettingsSegmentationConfigCreatePage />}
                />
                <Route
                  path="segmentation-config/edit/:segmentationConfigId"
                  element={<SettingsSegmentationConfigEditPage />}
                />
              </Route>
              <Route element={<SuperUserOutlet />}>
                <Route path="internal" element={<InternalPage />} />
                <Route
                  path="/settings/integrations/:platformCategory/:platform/:clientIntegrationType/run"
                  element={<RunIntegrationPage />}
                />
              </Route>
            </Route>
            <Route path="marketing-mix-modelling" element={<PocOutlet />}>
              <Route path="modelled-roi" element={<MmmModelledRoiPage />} />
              <Route
                path="budget-optimiser"
                element={<MmmBudgetOptimiserPage />}
              />
              <Route
                path="budget-scenario-planner"
                element={<BudgetScenarioPlanner />}
              />
              <Route
                path="campaign-budget-scenario-planner"
                element={<CampaignBudgetScenarioPlanner />}
              />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default App;
